import React, { useEffect, useState } from "react";
import { api, PROGRAM_LABELS } from "@/lib/api";
import { youtubeEmbed, resolveImg } from "@/lib/utils";

const sortT = (a, b) =>
  (a.display_order ?? 100) - (b.display_order ?? 100) ||
  String(b.created_at || "").localeCompare(String(a.created_at || ""));

const meta = (t) => [t.role, t.country].filter(Boolean).join(" · ");

function VideoCard({ t }) {
  const embed = youtubeEmbed(t.video_url);
  return (
    <div className="soft-card !p-0 overflow-hidden flex flex-col" data-testid={`video-testimonial-${t.id}`}>
      <div className="aspect-video bg-black">
        {embed ? (
          <iframe
            src={embed}
            title={t.author_name || "Video testimonial"}
            className="w-full h-full"
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <a href={t.video_url} target="_blank" rel="noreferrer" className="w-full h-full flex items-center justify-center text-white text-sm">Watch video</a>
        )}
      </div>
      <div className="p-5">
        {t.author_name && <div className="font-serif text-lg text-[#2D2422]">{t.author_name}</div>}
        {meta(t) && <div className="text-xs text-[#7E736E] mt-0.5">{meta(t)}</div>}
        {t.text && <p className="mt-2 text-sm text-[#3D3633] leading-relaxed">{t.text}</p>}
        {t.program_type && PROGRAM_LABELS[t.program_type] && (
          <div className="mt-3 text-[11px] uppercase tracking-[0.15em] text-terracotta">{PROGRAM_LABELS[t.program_type]}</div>
        )}
      </div>
    </div>
  );
}

function StaticCard({ t }) {
  const img = resolveImg(t.image_url) || resolveImg(t.thumbnail_url);
  return (
    <div className="soft-card !p-0 overflow-hidden flex flex-col bg-[#F3EFEA]" data-testid={`static-testimonial-${t.id}`}>
      {img ? (
        <img src={img} alt={t.author_name || "Testimonial"} loading="lazy"
          className="w-full object-cover max-h-[420px]" />
      ) : (
        <div className="p-6">
          <div className="text-terracotta font-serif text-3xl leading-none">"</div>
          <p className="font-serif text-lg italic text-[#2D2422] mt-2 leading-snug">{t.text}</p>
        </div>
      )}
      {(t.author_name || meta(t) || (img && t.text)) && (
        <div className="p-5 pt-4">
          {img && t.text && <p className="text-sm text-[#3D3633] leading-relaxed mb-2">{t.text}</p>}
          {t.author_name && <div className="font-medium text-[#2D2422] text-sm">{t.author_name}</div>}
          {meta(t) && <div className="text-xs text-[#7E736E]">{meta(t)}</div>}
        </div>
      )}
    </div>
  );
}

// preview=true renders only the latest 2-3 of each section (used on homepage).
export default function TestimonialsSections({ preview = false, limit = 3, showStatic = true }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/public/testimonials").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  if (items === null) return null;

  const published = items.slice().sort(sortT);
  let videos = published.filter((t) => t.video_url);
  let statics = showStatic ? published.filter((t) => !t.video_url && (t.image_url || t.thumbnail_url || t.text)) : [];
  if (preview) { videos = videos.slice(0, limit); statics = statics.slice(0, limit); }

  if (videos.length === 0 && statics.length === 0) {
    return (
      <div className="soft-card !p-8 text-center text-[#7E736E]" data-testid="testimonials-empty">
        Testimonials will appear here soon.
      </div>
    );
  }

  return (
    <div className="space-y-12" data-testid="testimonials-sections">
      {videos.length > 0 && (
        <div>
          <h3 className="font-serif text-2xl mb-4">Video Testimonials</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="video-testimonials-grid">
            {videos.map((t) => <VideoCard key={t.id} t={t} />)}
          </div>
        </div>
      )}
      {statics.length > 0 && (
        <div>
          <h3 className="font-serif text-2xl mb-4">Static Testimonials</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start" data-testid="static-testimonials-grid">
            {statics.map((t) => <StaticCard key={t.id} t={t} />)}
          </div>
        </div>
      )}
    </div>
  );
}
